import React, { useState, useEffect } from "react";
import {
  Card, CardBody, CardHeader,
  Badge, ListGroup, ListGroupItem,
  Spinner, Button
} from "reactstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import { Clock, Check, Play, Calendar } from "lucide-react";
import moment from "moment";

const DashboardTaskList = ({ limit = 5 }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState(null);
  const [timer, setTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);

  const fetchTasks = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/projects/task/my-tasks`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        params: { limit, sort: "due_date_time:asc" } // Get most urgent tasks first
      });
      setTasks(response.data.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
    setLoading(false);
  }, [limit]);

  const priorityBadge = (priority) => {
    const priorityMap = {
      low: "success",
      medium: "primary",
      high: "warning",
      urgent: "danger",
    };
    return (
      <Badge color={priorityMap[priority] || "primary"} className="text-uppercase">
        {priority}
      </Badge>
    );
  };

  const statusBadge = (status) => {
    const statusMap = {
      not_started: "secondary",
      in_progress: "info",
      completed: "success",
    };
    return (
      <Badge color={statusMap[status] || "secondary"} className="text-uppercase">
        {status.replace("_", " ")}
      </Badge>
    );
  };

  const formatDateTime = (dateTime) => {
    return dateTime ? moment(dateTime).format("MMM D, h:mm A") : "Not set";
  };

  const formatTime = (seconds) => {
    if (!seconds) return "0s";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    let timeStr = "";
    if (hours > 0) timeStr += `${hours}h `;
    if (minutes > 0 || hours > 0) timeStr += `${minutes}m `;
    timeStr += `${secs}s`;

    return timeStr.trim();
  };

  const startTask = (taskId) => {
    if (activeTask) return;

    setActiveTask(taskId);
    const interval = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 1000);
    setTimerInterval(interval);

    updateTaskStatus(taskId, "in_progress");
  };

  const finishTask = (taskId) => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    const totalTimeTaken = timer;
    setTimer(0);
    setActiveTask(null);

    updateTaskStatus(taskId, "completed", totalTimeTaken);
  };

  const updateTaskStatus = async (taskId, status, timeTaken = null) => {
    try {
      const payload = { status };
      if (timeTaken !== null) {
        payload.total_time_taken = timeTaken;
      }

      await axios.post(`${API_BASE_URL}/projects/task/update/${taskId}`, payload, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });

      fetchTasks(); // Refresh task list
    } catch (error) {
      console.error("Error updating task:", error);
    }
  };

  useEffect(() => {
    fetchTasks();
    return () => {
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [fetchTasks, timerInterval]);

  return (
    <Card className="h-100">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Recent Tasks</h5>
        <Link to="/staff/projectsandtasks/tasks" className="btn btn-sm btn-link">
          View All
        </Link>
      </CardHeader>
      <CardBody className="p-0">
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">No tasks found</p>
          </div>
        ) : (
          <ListGroup flush>
            {tasks.map((task) => (
              <ListGroupItem key={task.id} className="border-bottom">
                <div className="d-flex justify-content-between align-items-start mb-1">
                  <h6 className="mb-0 flex-grow-1">
                    <Link to={`/tasks`} className="text-dark">
                      {task.title}
                    </Link>
                  </h6>
                  <div>
                    {priorityBadge(task.priority)}
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center small text-muted mb-2">
                  <div className="d-flex align-items-center">
                    <Calendar size={14} className="me-1" />
                    {formatDateTime(task.due_date_time)}
                  </div>
                  {statusBadge(task.status)}
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  {task.status === "not_started" ? (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => startTask(task.id)}
                      disabled={activeTask !== null}
                    >
                      <Play size={12} className="me-1" /> Start
                    </Button>
                  ) : task.status === "in_progress" ? (
                    <>
                      {activeTask === task.id && (
                        <div className="d-flex align-items-center me-2">
                          <Clock size={12} className="me-1" />
                          {formatTime(timer)}
                        </div>
                      )}
                      <Button
                        color="success"
                        size="sm"
                        onClick={() => finishTask(task.id)}
                      >
                        <Check size={12} className="me-1" /> Finish
                      </Button>
                    </>
                  ) : (
                    <div className="text-success small">
                      Completed {task.total_time_taken && `in ${formatTime(task.total_time_taken)}`}
                    </div>
                  )}
                </div>
              </ListGroupItem>
            ))}
          </ListGroup>
        )}
      </CardBody>
    </Card>
  );
};

export default DashboardTaskList;